import React from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router-dom';

import i18next from 'i18next';

import { createGroup } from './GroupComponents/IdMixin.js';

import { GroupingIdMixin } from './GroupComponents/GroupingIdMixin.js';

import { GroupSubscribingComponent } from './GroupComponents/GroupSubscribingComponent.js';
import { InboxComponent, getGroupTypeTitle } from './GroupComponents/InboxComponent.js';
import { InboxButtonComponent } from './GroupComponents/InboxButtonComponent.js';

import DetailPage from './DetailPage.jsx';
import ChartPopup from './DetailPageComponents/ChartPopup.jsx';
import { ThresholdsPopup } from "./DetailPageComponents/ThresholdsPopup.jsx";

import { getNamedRoot, getTypeFromPath, sensorSubTypeToTitle, mapUnit, GROUP_TYPES } from './util/topic.js';

import { READ, WRITE } from './Om7Sense';

const REGEX_GROUP_ID = /(parent|group|focus|inbox;group|edit;[^;]+gparent);([^;]+)/;

export class DCPage extends React.Component {
    static contextTypes = {
        t: PropTypes.func.isRequired,
        p: PropTypes.object.isRequired,
        logged_in_as: PropTypes.string,
        gatewayName: PropTypes.string
    }

    DEFAULT_ROOT = "gtypes;DC";

    _isMounted = false;

    /** For all our components we manage subscriptions to sensor/UUID to not massively subscribe the same sensors in parallel */
    sensorSubscriptions = {};

    // TODO: we need a "saving report state"
    state = {
        isRedirecting: false,
        isCreatingReport: false,
        reportTopics: [],
        reportTopicData: {},
        reportSerial: 0,
        reportSaveState: null,
        reportName: "",
        subscription: false,
        subError: null,
        cols: [],
        colsPath: null,
        lastRenderedPath: null,
        editThr: null,
        canWriteGroups: false,
        canReadGroups: false,
        canCreateReport: false
    };

    constructor(props) {
        super(props);
    }

    subscribeSensor = (path) => {
        if (this.sensorSubscriptions[path]) {
            return;
        }

        return this.context.p.subscribePush(path).catch(e => {
            console.log("subscribeSensor could not find", path);
        });
        this.sensorSubscriptions[path] = true;
    }

    deleteGroup(setPath, groupId) {
        const p = this.context.p, t = this.context.t, self = this;

        const groupPath = groupId.indexOf("group/") === 0 ? groupId.substring(6) : groupId;

        const groupTopic = p.getPath("groupctrl/" + groupPath);
        const name = (groupTopic && groupTopic.name) ? groupTopic.name : "-";
        let text;
        if (groupTopic) {
            const groupType = groupTopic['type'];
            const groupTypeTitle = t("sensor_sub_types." + groupType?.toLowerCase());
            text = this.context.t("groups.confirm_delete__type__", {groupType: groupTypeTitle, name});
        } else {
            text = this.context.t("groups.confirm_delete__", {name});
        }
        

        if (window.confirm(text)) {
            console.log("deleting", groupId);
            p.put("groupctrl/" + groupPath, {"delete": true }).then(function (res) {
                console.log("Deleted successfully");
                setPath();
            }, function (err) {
                window.alert(t("groups.error_delete_failed"));
                console.error("Cannot delete", groupId, err);
            });
        }
    }

    showThresholdPopup (path) {
        this.setState({editThr: path});
    }

    locatePath = (path) => {
        if (!this._isMounted || !this.context.p.DS.groupctrl) {
            return;
        }

        const groupCtrlBase = this.context.p.DS.groupctrl;

        function checkForParent(thisPath, pathList) {
            pathList.push("group;" + thisPath);
            const groupCtrl = groupCtrlBase[thisPath];
            if (groupCtrl && groupCtrl.parent && groupCtrl.parent != 0) {
                // if (groupCtrl.parent === "0") {
                //     // This is the "active" DC
                //     pathL
                // }
                return checkForParent(groupCtrl.parent, pathList);
            }

            // We reached the parent, start with DC view
            pathList.push("gtypes;DC");
            return pathList;
        }

        const paths = checkForParent(path, []).reverse();

        // TODO: set error if path cannot be found

        return paths.join("%7C");
    }

    /**
     * If we should locate a certain DC item, check all preconditions
     * and adapt the browser path if found and show an error otherwise
     * @returns true if this is a locate command
     **/
    handleLocate = (path) => {
        const self = this;
        if (path.indexOf("locate;") === 0) {
             this.setState((state) => {
                
                // This request wants to find a valid DC view configuration to display one group and
                // all of its ancestors.
                const [ cmd, groupId ] = path.split(";");
                const newPath = self.locatePath(groupId);
                if (newPath) {
                    self.props.history.replace("/dc/" + newPath);
                    return {
                        isRedirecting: false
                    };
                }
                if (!state.isRedirecting) {
                    return {
                        isRedirecting: true
                    };
                }
            });
            return true;
        }
    }

    /**
     * Take the URL path and construct components to display
     * from them
     **/
    updateFromPath = (path) => {
        if (path === this.state?.colsPath?.join("|")) {
            return;
        }
        const self = this, p = this.context.p, t = this.context.t;
        const route = path.split("|").filter((item) => {
            return item && item !== "dc";
        });

        // Optimization: since we know the last path we have rendered in this state, see if we can keep some of the columns
        const lastRenderedRoute = this.state.colsPath ? this.state.colsPath : [];

        let cols = [];
        let actualPath = [];

        let currentType, lastType;
        let lastActiveId;
        let hasEdit = false;

        for (let routeIdx in route) {
            let idx = +routeIdx;
            let item = route[routeIdx];

            const isLastCol = idx === route.length - 1;
            const isFocusCol = idx === route.length - 2;
            const nextItemPath = !isLastCol && route[idx + 1];


            // We can reuse the columns from the last creation only under certain circumstances
            if (lastRenderedRoute[idx] === item && this.state.cols[routeIdx]) {
                // If this column was a detail column before, don't reuse
                    cols.push(this.state.cols[routeIdx]);
                    continue;
                // }
            }
            currentType = null;

            // An item can specify it's source topic type, which is typically only done for
            // the first item
            let topic;
            let hasIds = true;
            let groupId = -1;
            let parentGroupId = -1;
            let showInbox = false;
            let idToEdit = null;
            let sensorUuid = null;

            if (item.indexOf(";") !== -1) {
                const parts = item.split(";");


                if (parts[0] === "inbox") {
                    showInbox = true;
                    parts.splice(0, 1);
                    item = parts.join(";");
                } else if (parts[0] === "edit") {
                    if (isLastCol || isFocusCol) {
                        showInbox = true;
                        idToEdit = parts[1];
                        hasEdit = true;
                    }
                    parts.splice(0, 2);
                    item = parts.join(";");
                }

                if (parts[0] === "parent" || parts[0] === "group" || parts[0] === "gparent") {
                    topic = "gparent/";
                    groupId = parts[1];
                } else if (parts[0] === "type" || parts[0] === "gtypes") {
                    topic = "gtypes/";
                    currentType = parts[1];
                } else if (parts[0] === "sensor") {
                    sensorUuid = parts[1];
                    hasIds = false;
                } else {
                    continue;
                }
                topic += parts[1];
            } else {
                topic = "gparent/" + item;
            }
            if (!currentType) {
                if (groupId !== -1 && self.context && self.context.p) {
                    const groupTopic = self.context.p.getPath("groupctrl/" + groupId);
                    if (groupTopic && groupTopic.type) {
                        lastType = groupTopic.type;
                    }
                }
            }

            if (!currentType && sensorUuid === null) {
                if (lastType) {
                    let lastTypeIdx = GROUP_TYPES.indexOf(lastType);
                    if (lastTypeIdx != -1) {
                        currentType = GROUP_TYPES[lastTypeIdx + 1];
                    }
                }
            }

            let itemRendered = null;

            if (sensorUuid) {
                itemRendered = DetailPage;
                cols.push(itemRendered);
            }

            lastType = currentType;

            if (!itemRendered) {
                const groupTypeTitleKey = "sensor_sub_types." + currentType?.toLowerCase() + "_plur";
                const ItemInst = createGroup (GroupSubscribingComponent, groupTypeTitleKey, topic, "v");
                cols.push(ItemInst);
            }

            parentGroupId = groupId;
        }

        if (cols.length === 0) {
            const ItemInst = createGroup(GroupSubscribingComponent, currentType, this.DEFAULT_ROOT, "v", "");
            cols.push(ItemInst);
        }

        // Store as state if this page isn't mounted yet
        if (!this._isMounted) {
            this.state.cols = cols;
            this.state.hasEdit = hasEdit;
        } else {
            this.setState({
                cols: cols,
                colsPath: route,
                lastRenderedPath: path,
                hasEdit
            });
        }
    }

    onAuthenticated = () =>  {
        const self = this;
        const p = this.context.p;

        let promises = [
            this.context.p.subscribePush("groupctrl/*"),
            this.context.p.subscribePush("gtypes/*"),
            this.context.p.subscribePush("gparent/*"),
            this.context.p.subscribePush("group/+")
        ];
        this.setState({
            canCreateReport: this.context.p.checkPermission(WRITE, "reports"),
            canReadGroups: this.context.p.checkPermission(READ, "group"),
            canWriteGroups: this.context.p.checkPermission(WRITE, "group"),
        });

        Promise.all(promises).then(function (values) {
            if (!self._isMounted) {
                return;
            }
            self.setState({
                subscription: true
            });

            if (self.state.isRedirecting) {
                // If we're trying to redirect we were waiting until the DS paths are available.
                self.handleLocate(self.props.match.params.path);
            }
        }).catch(function (error) {
            if (!self._isMounted) {
                return;
            }
            self.setState({
                subscription: false,
                subError: error
            })
        });
    }

    toggleReport = (ev) => {
        const isChecked = ev.target.checked;

        this.setState({isCreatingReport: isChecked});
    }

    setReportName = (ev) => {
        this.setState({reportName: ev.target.value});
    }

    componentDidMount() {
        const self = this, c = this.context.p;
        this._isMounted = true;

        if (this.props.match.params && this.props.match.params.path) {
            if (!this.handleLocate(this.props.match.params.path)) {
                this.updateFromPath(this.props.match.params.path);
            }
        } else {
            this.props.history.replace("/dc/" + this.DEFAULT_ROOT);
        }

        if (p.isAuthenticated()) {
            this.onAuthenticated();
        } else {
            p.addListener("authenticated", this.onAuthenticated);
        }
    }

    componentDidUpdate(prevProps) {
        const newPath = this.props.match.params.path ? this.props.match.params.path : this.DEFAULT_ROOT;
        // console.log("splat", this.props.params, this.props.params?.splat);
        if (newPath !== this.state.lastRenderedPath) {
            if (!this.handleLocate(newPath)) {
                this.updateFromPath(newPath);
            }
        }
    }

    // shouldComponentUpdate(nextProps, nextState) {
    //     console.log("shouldComponentUpdate", this.props?.params?.splat, nextProps?.params?.splat);
    //     return this.props?.params?.splat !== nextProps?.params?.splat || this.state.subscription !== nextState.subscription;
    // }

    componentWillUnmount() {
        console.log("DCPage unmount");
        let promises = [
            this.context.p.unsubscribePush("groupctrl/*"),
            this.context.p.unsubscribePush("gtypes/*"),
            this.context.p.unsubscribePush("gparent/*"),
            this.context.p.unsubscribePush("group/+")
        ];

        for (let path in this.sensorSubscriptions) {
            promises.push(this.context.p.unsubscribePush(path));
        }

        this.sensorSubscriptions = [];

        Promise.all(promises).catch(function(error) {
            console.warn("Cannot unsubscribe in componentWillUnmount", error);
        });
        this._isMounted = false;
    }

    changeReportTopic = (isAdd, topic) => {
        const self = this;
        this.setState((state) => {
            let currentlySelected = state.reportTopics;

            if (isAdd) {
                if (currentlySelected.indexOf(topic) === -1) {
                    currentlySelected = state.reportTopics.concat([topic]);

                    // Now also subscribe to it's data, so we can get name, type, ...
                    const namedRootTopic = getNamedRoot(topic);
                    self.subscribeSensor(namedRootTopic);
                }
            } else {
                currentlySelected = state.reportTopics.filter(t => t !== topic);
            }
            return {"reportTopics": currentlySelected, reportSerial: state.reportSerial + 1};
        });
    }

    saveReport = (e) => {
        const self = this, t = this.context.t;
        e.preventDefault();

        const data = {name: this.state.reportName, topics: this.state.reportTopics};

        if (self.toastTimeout) {
            clearTimeout(self.toastTimeout);
        }
        self.toastTimeout = null;
        self.setState({
            saving: true,
            reportSaveState: null
        });

        function setToastTimeout () {
            if (self.toastTimeout) {
                clearTimeout(self.toastTimeout);
            }
            self.toastTimeout = setTimeout(() => {
                self.setState({reportSaveState: null})
                self.toastTimeout = null;
            }, 4000);
        }

        this.context.p.put("/reports", data).then((res) => {
            self.setState({
                reportName:"",
                reportTopics:[],
                isCreatingReport: false,
                reportSaveState: {
                    success: true,
                    msg: t("reporting.report__created", {reportTitle: data.name})}});
            setToastTimeout();
        }, (err) => {
            console.warn("cannot create report", err);
            self.setState({
                reportSaveState: {
                    success: false,
                    msg: t("reporting.report__creation_error__", {reportTitle: data.name, error: err})}});
            setToastTimeout();
        });
    }

    render() {
        if (!this.state.subscription || !this.state.colsPath || this.state.isRedirecting) {
            return null;
        } else {
            const self = this, p = this.context.p, t = this.context.t;
            let route = this.state.colsPath.slice();
            let colComponents = this.state.cols;

            let cols = [];

            let routeLength = 0;
            for (let path of route) {
                // Ignore "focus" parts, they're invisible and just there to show the preceding item what it should mark as active
                if (path.indexOf("focus;") !== 0) {
                    routeLength++;
                }
            }
            // For sm, we show one small col and the details
            const smCutOff = routeLength - 2;
            const mdCutOff = smCutOff;
            const lgCutOff = mdCutOff - 1;
            const xlCutOff = mdCutOff - 1;
            const aboveXlCutOff = xlCutOff - 2;

            if (aboveXlCutOff > 0) {
                route = route.slice(aboveXlCutOff);
                colComponents = colComponents.slice(aboveXlCutOff);
            }

            const pushPath = function(idx) {
                return function(topicToPush, endEdit) {
                    if (self.state.hasEdit && !endEdit) {
                        if (!window.confirm(self.context.t("groups.confirm_cancel_edit"))) {
                            return;
                        }
                    }
                    if (topicToPush && topicToPush.indexOf("sensor;") === 0) {
                        self.props.history.push("/devices/" + topicToPush.substring(7));
                        return;
                    }
                    let pathToHere;
                    if (idx > -1) {
                        const pathsToIdx = route.slice(0, idx + 1);
                        const newItems = [];
                        pathsToIdx.forEach((item) => {
                            // We cannot have "edit" items that are not at the end
                            if (item && item.indexOf("edit") === 0) {
                                const editParts = item.split(";");
                                editParts.splice(0, 2);
                                item = editParts.join(";");
                            }
                            if (item && item !== "dc") {
                                newItems.push(item);
                            }
                        });
                        pathToHere = newItems.join("|");
                    } else {
                        pathToHere = "";
                    }

                    self.props.history.push("/dc/" + pathToHere + (topicToPush ? "|" + topicToPush.replace("/", ";") : ""));
                };
            };

            function createPushComponent() {
                return function(componentToPush) {
                    self.props.history.push("/dc/" + self.props.match.params.path + "/" + componentToPush);
                }
            }

            const replacePath = function(newPath) {
                return function() {
                    self.props.history.replace(newPath);
                };
            };

            const getGroupIdFromPath = function(path) {
                let match = REGEX_GROUP_ID.exec(path);
                if (match) {
                    return match[2];
                }
                return null;
            }

            function getColClassName(idx) {
                let classNames = "column overview hide-xs col-1";

                if (idx >= smCutOff) {
                    classNames += " col-sm-4";
                } else {
                    classNames += " hide-sm";
                }

                if (idx >= mdCutOff) {
                    classNames += " col-md-3";
                } else {
                    classNames += " hide-md";
                }

                if (idx >= lgCutOff) {
                    classNames += " col-lg-3";
                } else {
                    classNames += " hide-lg";
                }

                if (idx >= xlCutOff) {
                    classNames += " col-xl-2";
                    classNames += " col-2x-2";
                } else {
                    classNames += " hide-xl";
                    classNames += " hide-2x";
                }

                return classNames;
            }

            function getDetailColClassName(idx) {
                let classNames = "details focus col-xs-12 column"
                if (idx > 0) {
                    classNames += " col-sm-8 col-md-9";
                }
                let xlCols = (12 - (idx * 2));
                if (xlCols < 8) {
                    xlCols = 8;
                }
                let lgCols = (12 - (idx * 3))
                if (lgCols < 6) {
                    lgCols = 6;
                }
                classNames += " col-lg-" + lgCols + " col-xl-" + xlCols + " col-2x-" + xlCols + " col-" + (12 - idx);
                return classNames;
            }

            let currentType, lastType;
            let lastActiveId;

            for (let routeIdx in route) {
                let idx = +routeIdx;
                let item = route[routeIdx];

                let isLastCol = idx === route.length - 1;
                let isFocusCol = (route.length === 1 || idx === route.length - 2);
                const nextItemPath = !isLastCol && route[idx + 1];

                // Inbox or detail page demand a full page even when they're the last item
                if (nextItemPath && (nextItemPath.indexOf("sensor") === 0 || nextItemPath.indexOf("inbox") === 0 || nextItemPath.indexOf("edit") === 0)) {
                    isFocusCol = false;
                }

                // The focus path part is invisible; it's just there to mark something as selected
                if (nextItemPath && (nextItemPath.indexOf("focus") === 0)) {
                    isLastCol = true;
                }

                currentType = null;

                // An item can specify it's source topic type, which is typically only done for
                // the first item
                let topic;
                let hasIds = true;
                let groupId = -1;
                let parentGroupId = -1;
                let showInbox = false;
                let idToEdit = null;
                let sensorUuid = null;

                if (item.indexOf(";") !== -1) {
                    const parts = item.split(";");

                    if (parts[0] === "inbox") {
                        showInbox = true;
                        parts.splice(0, 1);
                        item = parts.join(";");
                        if (isLastCol) {
                            isFocusCol = true;
                        }
                    } else if (parts[0] === "edit") {
                        if (isFocusCol || isLastCol) {
                            showInbox = true;
                            idToEdit = parts[1];
                        }
                        parts.splice(0, 2);
                        item = parts.join(";");
                        if (isLastCol) {
                            isFocusCol = true;
                        }
                    }

                    if (parts[0] === "parent" || parts[0] === "group" || parts[0] === "gparent") {
                        topic = "gparent/";
                        groupId = parts[1];
                    } else if (parts[0] === "type" || parts[0] === "gtypes") {
                        topic = "gtypes/";
                        currentType = parts[1];
                    } else if (parts[0] === "sensor") {
                        sensorUuid = parts[1];
                        hasIds = false;
                    } else {
                        continue;
                    }
                    topic += parts[1];
                } else {
                    topic = "gparent/" + item;
                }
                if (!currentType) {
                    if (groupId !== -1 && self.context && self.context.p) {
                        const groupTopic = self.context.p.getPath("groupctrl/" + groupId);
                        if (groupTopic && groupTopic.type) {
                            lastType = groupTopic.type;
                        }
                    }
                }

                if (!currentType && sensorUuid === null) {
                    if (lastType) {
                        let lastTypeIdx = GROUP_TYPES.indexOf(lastType);
                        if (lastTypeIdx != -1) {
                            currentType = GROUP_TYPES[lastTypeIdx + 1];
                        }
                    }
                }

                let constituents = null;

                let itemRendered = null;

                let classNames = "column col-";
                // Active group id, taken from next column
                let activeId = nextItemPath ? getGroupIdFromPath(nextItemPath) : null;
                if (idToEdit) {
                    activeId = idToEdit;
                }
                let inbox = null;

                if (isLastCol) {
                    if (sensorUuid) {
                        classNames = getDetailColClassName(idx);
                        itemRendered = showInbox ? 
                        (
                            <InboxComponent key={"create-" + currentType + "-" + (idToEdit ? idToEdit : lastActiveId)} lastPath={pushPath(idx).bind(this)} subscribeSensor={this.subscribeSensor} groupType={currentType} groupId={idToEdit ? idToEdit : lastActiveId} isEdit={idToEdit !== null} showOpen={showInbox} />
                        ) : (
                            <DetailPage params={{uuid:sensorUuid}} />
                        );
                    }
                }

                // A focused column takes the whole width
                if (isFocusCol) {
                    classNames = getDetailColClassName(idx + (isLastCol ? 0 : 1));

                    if (!sensorUuid) {
                        if (idToEdit) {
                            constituents = (
                                <InboxComponent key={"create-" + currentType + "-" + (idToEdit ? idToEdit : lastActiveId)} lastPath={pushPath(idx).bind(this)} subscribeSensor={this.subscribeSensor} groupType={currentType} groupId={idToEdit ? idToEdit : lastActiveId} isEdit={idToEdit !== null} showOpen={showInbox} />
                            );
                        } else {
                            if (activeId) {
                                let parentName = "-";
                                const groupTopic = p.getPath("groupctrl/" + activeId);
                                const name = (groupTopic && groupTopic.name) ? groupTopic.name : "-";
                                let groupType = lastType;
                                if (groupTopic) {
                                    const parentGroupTopic = p.getPath("groupctrl/" + groupTopic.parent);
                                    parentName = (parentGroupTopic && parentGroupTopic.name) ? parentGroupTopic.name : "-";
                                    groupType = groupTopic['type'];
                                }

                                constituents = (
                                    <GroupingIdMixin isCreatingReport={this.state.isCreatingReport}
                                        reportTopics={this.state.reportTopics} showThresholdPopup={this.showThresholdPopup} changeReportTopic={this.changeReportTopic} reportSerial={this.state.reportSerial} key={"groupidctrl/" + activeId}
                                        pushCB={pushPath(idx)} pushComponentCB={createPushComponent()} idPath={"groupctrl/" + activeId} resultPath={"group/" + activeId} idSubPath = "include" subscribeSensor={this.subscribeSensor} />
                                );
                            }
                            if (currentType && this.state.canWriteGroups) {
                                inbox = (
                                    <InboxComponent key={"create-" + currentType + "-" + (idToEdit ? idToEdit : lastActiveId)} lastPath={pushPath(idx).bind(this)} subscribeSensor={this.subscribeSensor} groupType={currentType} groupId={idToEdit ? idToEdit : lastActiveId} isEdit={idToEdit !== null} showOpen={showInbox} />
                                );
                            }
                        }
                    }
                } else if (!itemRendered) {
                    classNames = getColClassName(idx);
                    
                    if (currentType) {
                        inbox = (
                            <InboxButtonComponent key={"create-" + currentType} disabled={!this.state.canWriteGroups} pushCB={pushPath(idx - 1).bind(this, "inbox;" + item)} groupType={currentType} activeId={lastActiveId} isEdit={false} />
                        );
                    }
                }

                // See if it is a relative path (to group/) or absolute
                if (activeId && (typeof activeId === "number" || activeId.indexOf("/") === -1)) {
                    activeId = "group/" + activeId;
                }

                if (!itemRendered) {
                    const ItemInst = colComponents[routeIdx];
                    itemRendered = (
                        <ItemInst key={"frg" + topic} pushCB={pushPath(idx)} pushEditCB={pushPath(idx - 1)}
                                showThresholdPopup={this.showThresholdPopup} pushComponentCB={createPushComponent()} activePath={activeId}
                                activeDetails={constituents} subscribeSensor={this.subscribeSensor} isDetails={isFocusCol}
                                isReadOnly={!this.state.canWriteGroups}
                                deleteCB={self.deleteGroup.bind(self, pushPath(idx - 1))} isEdit={self.state.hasEdit} />
                    );
                }

                // Deduce and translate the header for the whole column.
                // This is at least the type of elements in this column and if applicable the reference
                // to the parent
                const groupTypeTitleKey = "sensor_sub_types." + currentType?.toLowerCase() + "_plur";
                let hasParent = lastActiveId !== -1 && lastActiveId !== undefined;
                let currentTypeHeader;
                const thisGroupLabel = i18next.exists(groupTypeTitleKey + "_short") ? t(groupTypeTitleKey + "_short") : t(groupTypeTitleKey);

                if (hasParent) {
                    const parentGroupTypeTitleKey = "sensor_sub_types." + lastType?.toLowerCase();
                    const parentGroupLabel = i18next.exists(parentGroupTypeTitleKey + "_short") ? t(parentGroupTypeTitleKey + "_short") : t(parentGroupTypeTitleKey);
                    const parentGrp = this.context.p.getPath(lastActiveId);
                    if (parentGrp && parentGrp.name !== undefined) {
                        currentTypeHeader = t("groups.__in__", {groupType: thisGroupLabel, parentGroupType: parentGroupLabel, parent: parentGrp.name});
                    } else {
                        hasParent = false;
                    }
                }
                
                if (!hasParent) {
                    currentTypeHeader = t(groupTypeTitleKey);
                }

                lastType = currentType;

                let column = (                    
                    <div key={"dccol-" + idx} className={classNames}>
                        <span className="currentType">
                            { currentTypeHeader }
                        </span>
                        
                        { itemRendered }
                        { inbox }
                        
                    </div>
                )
                cols.push(column);
                parentGroupId = groupId;
                lastActiveId = activeId;
            }

            if (cols.length === 0) {
                const ItemInst = colComponents[0];
                cols.push((
                    <div key="dccol-0" className="column col-1 overview">
                        <ItemInst pushCB={pushPath(-1)} showThresholdPopup={this.showThresholdPopup} subscribeSensor={this.subscribeSensor}
                             isDetails={false} deleteCB={self.deleteGroup.bind(self, pushPath(idx - 1))}
                             isReadOnly={!this.state.canWriteGroups} />
                    </div>
                ));
            }
            const topicChips = [];

            for (let i in this.state.reportTopics) {
                const reportTopic = this.state.reportTopics[i];
                const topicSplit = reportTopic.split("/");
                const namedRootTopic = getNamedRoot(reportTopic);
                const data = this.context.p.getPath(namedRootTopic);
                const typeCode = getTypeFromPath(this.context.p, reportTopic, data);
                const type = sensorSubTypeToTitle[typeCode];
                const unit = mapUnit[topicSplit[topicSplit.length - 1]];

                function removeCallback(e) {
                    self.changeReportTopic(false, reportTopic);
                }
                const chip = (
                    <span className="chip" key={"chip-" + reportTopic}>
                        <figure className="avatar avatar-sm" data-initial={unit} style={{backgroundColor: "#5755d9"}} />
                        { type + " " + (data?.name || "-") }
                        <a onClick={removeCallback} className="btn btn-clear" aria-label="Close" role="button"></a>
                    </span>
                );
                topicChips.push(chip);
            }
            let reportToast = null;

            if (self.state.reportSaveState) {
                function clearToast(e) {
                    if (self.toastTimeout) {
                        self.clearTimeout(self.toastTimeout);
                    }
                    self.toastTimeout = null;
                    self.setState({reportSaveState: null});
                }
                let classNames = "toast " + (self.state.reportSaveState.success ? "toast-success" : "toast-error");

                reportToast = (
                    <div className={classNames}>
                        <button className="btn btn-clear float-right" onClick={clearToast}></button>
                        {self.state.reportSaveState.msg}
                    </div>);

            }
            let thresholdPopup = null;

            if (this.state.editThr) {
                thresholdPopup = <ThresholdsPopup thrPath={this.props.match?.splat} history={this.props.history} location={this.props.location} router={this.props.history} />;
            }

            // TODO: when saving report make the button unclickable and show spinner
            return (
                <div className="main_bottom_footer">
                    {reportToast}
                    { this.state.canCreateReport && (
                        <div className="reporting">
                            <div className="columns">
                                <div className="col-2 column">
                                    <input id="createReport" type="checkbox" checked={this.state.isCreatingReport} onChange={this.toggleReport} />
                                    &nbsp;<label htmlFor="createReport">Create report?</label>
                                </div>
                                { this.state.isCreatingReport && (
                                    <>
                                        <div className="col-5 column">
                                            <h4>Step 1</h4>
                                            <p>Please select the data sources for which you want to create a report from. You can select them via checkboxes directly here below.</p>
                                            <p>These data sources have been selected so far:</p>
                                            <div>{ topicChips }</div>
                                        </div>
                                        <div className="col-5 column">
                                            <h4>Step 2</h4>
                                            <p>Now complete your report by giving at a unique name and save it. Afterwards you can see your saved reports under the "Reports" tab in the main navigation.</p>
                                            <div className="input-group">
                                                <span className="input-group-addon">{t('reporting.name')}</span>
                                                <input type="text" className="form-input" placeholder="" value={this.state.reportName} onChange={this.setReportName} />
                                                <input type="submit" className="btn btn-primary input-group-btn" defaultValue="submit" onClick={this.saveReport} />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <div key="dcRoot" className="content columns col-gapless dcpage">
                        { cols }
                    </div>

                    { thresholdPopup }
                    <Route path="/dc/:path?/chart-:popup_path" component={ChartPopup} />
                </div>
            );
        }
    }
}